import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, MessageCircle } from "lucide-react";
import { TRIAL_WA } from "../data/content";

const SECTIONS = [
  { hash: "#home", label: "Home" },
  { hash: "#why-zavi", label: "Why Zavi" },
  { hash: "#training-groups", label: "Training Groups" },
  { hash: "#pathway", label: "Player Pathway" },
  { hash: "#centres", label: "Our Centres" },
  { hash: "#reviews", label: "Testimonials" },
  { hash: "#contact", label: "Contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === "/";

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const nav = (hash) => {
    setOpen(false);
    if (isHome) {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      window.location.href = `/${hash}`;
    }
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/85 backdrop-blur-xl border-b border-white/10" data-testid="navbar">
        <div className="max-w-7xl mx-auto px-5 lg:px-12 h-20 sm:h-24 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo" onClick={() => setOpen(false)}>
            <img src="/logo-mark.png" alt="Zavi Football Club" className="h-16 sm:h-20 w-auto" />
            <span className="font-heading text-xl sm:text-3xl font-black tracking-tighter text-white leading-none">
              ZAVI<span className="text-[#D4AF37]">FC</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-7">
            {SECTIONS.slice(0, 5).map((s) => (
              <button key={s.hash} onClick={() => nav(s.hash)}
                className="text-[11px] tracking-[0.2em] uppercase font-semibold text-white/70 hover:text-[#D4AF37] transition-colors duration-200"
                data-testid={`nav-desktop-${s.hash.slice(1)}`}>
                {s.label}
              </button>
            ))}
            <Link to="/login" className="text-[11px] tracking-[0.2em] uppercase font-semibold text-white/70 hover:text-[#D4AF37]" data-testid="nav-desktop-login">Login</Link>
            <a href={TRIAL_WA} target="_blank" rel="noreferrer" data-testid="nav-desktop-trial"
              className="bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-[11px] px-5 py-2.5 hover:bg-[#F3D059] transition-colors duration-200">
              Book Free Trial
            </a>
          </nav>

          {/* Mobile controls */}
          <div className="flex lg:hidden items-center gap-2">
            <a href={TRIAL_WA} target="_blank" rel="noreferrer" data-testid="nav-mobile-trial"
              className="bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-[10px] px-3 py-2 hidden xs:inline-block">
              Free Trial
            </a>
            <button
              className="text-white hover:text-[#D4AF37] transition-colors duration-200 p-1"
              onClick={() => setOpen(!open)}
              data-testid="nav-menu-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>
      </header>

      {/* Slide-in mobile menu */}
      <div
        className={`fixed inset-0 z-40 bg-[#050505] transition-transform duration-300 lg:hidden ${open ? "translate-x-0" : "translate-x-full"}`}
        data-testid="nav-menu-overlay"
      >
        <div className="h-full flex flex-col pt-24 pb-10 px-6 overflow-y-auto">
          <nav className="flex flex-col gap-4 flex-1">
            {SECTIONS.map((s) => (
              <button
                key={s.hash}
                onClick={() => nav(s.hash)}
                data-testid={`nav-mobile-${s.hash.slice(1)}`}
                className="text-left font-heading text-2xl font-black uppercase tracking-tight text-white hover:text-[#D4AF37] transition-colors duration-200 border-b border-white/8 py-3"
              >
                {s.label}
              </button>
            ))}
          </nav>
          <div className="mt-6 space-y-3">
            <a
              href={TRIAL_WA}
              target="_blank"
              rel="noreferrer"
              onClick={() => setOpen(false)}
              data-testid="nav-menu-trial"
              className="w-full block bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-sm px-6 py-4 text-center transition-all duration-200 hover:bg-[#F3D059] gold-glow"
            >
              Book a Free Trial
            </a>
            <Link
              to="/login"
              onClick={() => setOpen(false)}
              data-testid="nav-menu-login"
              className="w-full block border border-white/20 text-white font-bold uppercase tracking-widest text-sm px-6 py-3.5 text-center hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-200"
            >
              Player Login
            </Link>
            <a href={TRIAL_WA.replace("book a free trial", "chat with the club")} target="_blank" rel="noreferrer"
              onClick={() => setOpen(false)}
              className="w-full flex items-center justify-center gap-2 text-white/50 text-xs uppercase tracking-widest py-2 hover:text-[#D4AF37]"
              data-testid="nav-menu-whatsapp">
              <MessageCircle size={14} /> WhatsApp us
            </a>
          </div>
        </div>
      </div>
    </>
  );
};
